import React, { useState, useRef } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Grid,
  FormHelperText,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LoadingBar from "react-top-loading-bar";
import { toast } from "react-toastify";
import Logo from "../../images/sbg_logo.webp";
import DataService from "../../services/data.service";

const JobApplication = () => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    ssn: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",

    // Emergency Contact
    emergencyContact: "",
    emergencyPhone: "",
    emergencyContactRelationship: "",

    // Licenses
    armedStatus: "",
    licenseNumber: "",
    licenseExpiry: "",
    licenseState: "",

    // Employment
    previousSecurityClearance: "",

    // Military
    militaryServed: false,
    militaryBranch: "",
    militaryRank: "",
    militaryStartDate: "",
    militaryEndDate: "",

    // Education
    highSchoolDiploma: false,

    // Certifications
    hasFirearmsLicense: false,
    firearmsLicenseNumber: "",
    firearmsLicenseState: "",
    firearmsLicenseExpiry: "",
    hasFirstAid: false,
    firstAidOrg: "",
    firstAidDate: "",
    firstAidExpiry: "",
    hasCPR: false,
    cprOrg: "",
    cprDate: "",
    cprExpiry: "",

    // Background
    hasCriminalHistory: false,
    criminalHistoryDetails: "",
    fingerprintingCompleted: false,
    backgroundCheckAuthorized: false,

    // Health
    physicalLimitations: "",
    mentalHealthDisclosure: "",
    substanceAbuseDisclosure: "",

    // Availability
    hoursPerWeek: "",
    availableStartDate: "",
  });

  const [preferredShifts, setPreferredShifts] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);

  const [previousEmployment, setPreviousEmployment] = useState([
    { jobTitle: "", companyName: "", startDate: "", endDate: "", reasonForLeaving: "" }
  ]);

  const [collegeEducation, setCollegeEducation] = useState([
    { institution: "", degree: "", graduationYear: "" }
  ]);

  const [securityTraining, setSecurityTraining] = useState([
    { trainingName: "", organization: "", completionDate: "" }
  ]);

  const [references, setReferences] = useState([
    { name: "", title: "", company: "", phone: "", email: "", relationship: "" }
  ]);

  const [files, setFiles] = useState({
    photo: null,
    guardCardDocument: null,
    driverLicenseDocument: null,
  });

  const [errors, setErrors] = useState({});

  const shifts = ["Morning (6AM-2PM)", "Afternoon (2PM-10PM)", "Night (10PM-6AM)", "Flexible"];
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const truncateFileName = (name, maxLength = 20) => {
    if (!name || name.length <= maxLength) return name;
    const ext = name.split('.').pop();
    const nameWithoutExt = name.slice(0, name.lastIndexOf('.'));
    const truncated = nameWithoutExt.slice(0, maxLength - ext.length - 4);
    return `${truncated}...${ext}`;
  };



  const resetForm = () => {
    setFormData({
      firstName: "", middleName: "", lastName: "", dateOfBirth: "", ssn: "", email: "", phone: "",
      address: "", city: "", state: "", zipCode: "",
      emergencyContact: "", emergencyPhone: "", emergencyContactRelationship: "",
      armedStatus: "", licenseNumber: "", licenseExpiry: "", licenseState: "",
      previousSecurityClearance: "",
      militaryServed: false, militaryBranch: "", militaryRank: "", militaryStartDate: "", militaryEndDate: "",
      highSchoolDiploma: false,
      hasFirearmsLicense: false, firearmsLicenseNumber: "", firearmsLicenseState: "", firearmsLicenseExpiry: "",
      hasFirstAid: false, firstAidOrg: "", firstAidDate: "", firstAidExpiry: "",
      hasCPR: false, cprOrg: "", cprDate: "", cprExpiry: "",
      hasCriminalHistory: false, criminalHistoryDetails: "",
      fingerprintingCompleted: false, backgroundCheckAuthorized: false,
      physicalLimitations: "", mentalHealthDisclosure: "", substanceAbuseDisclosure: "",
      hoursPerWeek: "", availableStartDate: "",
    });
    setPreferredShifts([]);
    setAvailableDays([]);
    setPreviousEmployment([{ jobTitle: "", companyName: "", startDate: "", endDate: "", reasonForLeaving: "" }]);
    setCollegeEducation([{ institution: "", degree: "", graduationYear: "" }]);
    setSecurityTraining([{ trainingName: "", organization: "", completionDate: "" }]);
    setReferences([{ name: "", title: "", company: "", phone: "", email: "", relationship: "" }]);
    setFiles({ photo: null, guardCardDocument: null, driverLicenseDocument: null });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      setFiles((prev) => ({ ...prev, [name]: fileList[0] }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleArrayChange = (setter) => (index, field, value) => {
    setter(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addArrayItem = (setter, template) => () => {
    setter(prev => [...prev, { ...template }]);
  };

  const removeArrayItem = (setter) => (index) => {
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const toggleArrayValue = (arr, setArr, value) => {
    if (arr.includes(value)) {
      setArr(arr.filter(v => v !== value));
    } else {
      setArr([...arr, value]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone must be 10 digits";
    }
    if (!formData.licenseNumber.trim()) newErrors.licenseNumber = "License number is required";
    if (!formData.armedStatus) newErrors.armedStatus = "Please select armed status";
    if (!files.guardCardDocument) newErrors.guardCardDocument = "Guard card is required";
    if (!formData.backgroundCheckAuthorized) newErrors.backgroundCheckAuthorized = "Background check authorization is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setLoading(true);
    ref.current.continuousStart();

    try {
      const submitData = new FormData();

      // Add simple fields
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== "" && formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });

      // Add complex objects as JSON strings
      submitData.append("previousEmployment", JSON.stringify(previousEmployment.filter(e => e.jobTitle || e.companyName)));
      submitData.append("collegeEducation", JSON.stringify(collegeEducation.filter(e => e.institution)));
      submitData.append("securityTraining", JSON.stringify(securityTraining.filter(e => e.trainingName)));
      submitData.append("references", JSON.stringify(references.filter(r => r.name)));

      // Military service as object
      submitData.append("militaryService", JSON.stringify({
        served: formData.militaryServed,
        branch: formData.militaryBranch,
        rank: formData.militaryRank,
        startDate: formData.militaryStartDate,
        endDate: formData.militaryEndDate
      }));

      // Certifications as objects
      submitData.append("firearmsLicense", JSON.stringify({
        hasLicense: formData.hasFirearmsLicense,
        licenseNumber: formData.firearmsLicenseNumber,
        state: formData.firearmsLicenseState,
        expiryDate: formData.firearmsLicenseExpiry
      }));
      submitData.append("firstAidCertification", JSON.stringify({
        hasCertification: formData.hasFirstAid,
        organization: formData.firstAidOrg,
        certificationDate: formData.firstAidDate,
        expiryDate: formData.firstAidExpiry
      }));
      submitData.append("cprCertification", JSON.stringify({
        hasCertification: formData.hasCPR,
        organization: formData.cprOrg,
        certificationDate: formData.cprDate,
        expiryDate: formData.cprExpiry
      }));

      // Availability as object
      submitData.append("availability", JSON.stringify({
        hoursPerWeek: formData.hoursPerWeek,
        preferredShifts: preferredShifts,
        availableDays: availableDays,
        startDate: formData.availableStartDate
      }));

      // Add files
      Object.keys(files).forEach((key) => {
        if (files[key]) submitData.append(key, files[key]);
      });

      const response = await DataService.submitApplication(submitData);

      if (response.data.success) {
        resetForm();
        setSubmitted(true);
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Submission failed";
      toast.error(message);
    } finally {
      setLoading(false);
      ref.current.complete();
    }
  };

  if (submitted) {
    return (
      <>
        <LoadingBar color="#084e8a" ref={ref} height={5} />
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5", py: 4 }}>
          <Paper elevation={3} sx={{ p: 5, borderRadius: 2, textAlign: "center", maxWidth: 500 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
              Application Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Thank you for applying to join Security Base Group. We have received your application and will review it shortly.
              <strong> A confirmation email has been sent to your email address.</strong>
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
              Our team will contact you within 2-3 business days. Please keep your phone accessible.
            </Alert>
            <Button variant="contained" onClick={() => setSubmitted(false)}>
              Submit Another Application
            </Button>
          </Paper>
        </Box>
      </>
    );
  }

  const SectionTitle = ({ children }) => (
    <Typography variant="h6" gutterBottom sx={{ mt: 3, borderBottom: "2px solid #1976d2", pb: 1 }}>
      {children}
    </Typography>
  );

  return (
    <>
      <LoadingBar color="#084e8a" ref={ref} height={5} />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: { xs: 3, md: 5 } }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-md-12">
              <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                <Box textAlign="center" mb={3}>
                  <img src={Logo} alt="SBG Logo" style={{ height: 80, marginBottom: 16, maxWidth: "100%", objectFit: "contain" }} />
                  <Typography variant="h4" fontWeight="bold" color="primary" sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
                    Security Guard Job Application
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Join our team of professional security officers
                  </Typography>

                </Box>

                <form onSubmit={handleSubmit}>
                  <SectionTitle>Personal Information</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="First Name *" name="firstName" value={formData.firstName} onChange={handleChange} error={!!errors.firstName} helperText={errors.firstName} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Middle Name" name="middleName" value={formData.middleName} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Last Name *" name="lastName" value={formData.lastName} onChange={handleChange} error={!!errors.lastName} helperText={errors.lastName} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Date of Birth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="SSN (Last 4 Digits)" name="ssn" value={formData.ssn} onChange={handleChange} inputProps={{ maxLength: 4 }} placeholder="XXXX" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Email *" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField fullWidth label="Phone *" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} />
                    </Grid>
                  </Grid>
                  <SectionTitle>Address</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Street Address" name="address" value={formData.address} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="City" name="city" value={formData.city} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="State" name="state" value={formData.state} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Zip Code" name="zipCode" value={formData.zipCode} onChange={handleChange} />
                    </Grid>
                  </Grid>
                  <SectionTitle>Emergency Contact</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Contact Name" name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Contact Phone" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Relationship" name="emergencyContactRelationship" value={formData.emergencyContactRelationship} onChange={handleChange} />
                    </Grid>
                  </Grid>
                  <SectionTitle>Employment History</SectionTitle>
                  {previousEmployment.map((emp, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2.5}>
                          <TextField fullWidth label="Job Title" value={emp.jobTitle} onChange={(e) => handleArrayChange(setPreviousEmployment)(index, "jobTitle", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={2.5}>
                          <TextField fullWidth label="Company Name" value={emp.companyName} onChange={(e) => handleArrayChange(setPreviousEmployment)(index, "companyName", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth label="Start Date" type="date" value={emp.startDate} onChange={(e) => handleArrayChange(setPreviousEmployment)(index, "startDate", e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth label="End Date" type="date" value={emp.endDate} onChange={(e) => handleArrayChange(setPreviousEmployment)(index, "endDate", e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                          <TextField fullWidth label="Reason" value={emp.reasonForLeaving} onChange={(e) => handleArrayChange(setPreviousEmployment)(index, "reasonForLeaving", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={2} sm={1}>
                          {previousEmployment.length > 1 && (
                            <IconButton color="error" onClick={() => removeArrayItem(setPreviousEmployment)(index)} sx={{ p: 0.5 }}><RemoveCircleOutlineIcon /></IconButton>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button startIcon={<AddCircleOutlineIcon />} onClick={addArrayItem(setPreviousEmployment, { jobTitle: "", companyName: "", startDate: "", endDate: "", reasonForLeaving: "" })} size="small">
                    Add Employment
                  </Button>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Previous Security Clearance (if any)" name="previousSecurityClearance" value={formData.previousSecurityClearance} onChange={handleChange} />
                    </Grid>
                  </Grid>
                  <SectionTitle>Military Service</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.militaryServed} onChange={handleChange} name="militaryServed" />} label="I have served in the military" />
                    </Grid>
                    {formData.militaryServed && (
                      <>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Branch" name="militaryBranch" value={formData.militaryBranch} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Rank" name="militaryRank" value={formData.militaryRank} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Start Date" type="date" name="militaryStartDate" value={formData.militaryStartDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="End Date" type="date" name="militaryEndDate" value={formData.militaryEndDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <SectionTitle>Education & Training</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.highSchoolDiploma} onChange={handleChange} name="highSchoolDiploma" />} label="High School Diploma / GED" />
                    </Grid>
                  </Grid>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>College/University Education (Optional)</Typography>
                  {collegeEducation.map((edu, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField fullWidth label="Institution" value={edu.institution} onChange={(e) => handleArrayChange(setCollegeEducation)(index, "institution", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Degree" value={edu.degree} onChange={(e) => handleArrayChange(setCollegeEducation)(index, "degree", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                          <TextField fullWidth label="Year" value={edu.graduationYear} onChange={(e) => handleArrayChange(setCollegeEducation)(index, "graduationYear", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={2} sm={1}>
                          {collegeEducation.length > 1 && <IconButton color="error" onClick={() => removeArrayItem(setCollegeEducation)(index)}><RemoveCircleOutlineIcon /></IconButton>}
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button startIcon={<AddCircleOutlineIcon />} onClick={addArrayItem(setCollegeEducation, { institution: "", degree: "", graduationYear: "" })} size="small">Add Education</Button>

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Security Training (Optional)</Typography>
                  {securityTraining.map((trn, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Training Name" value={trn.trainingName} onChange={(e) => handleArrayChange(setSecurityTraining)(index, "trainingName", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Organization" value={trn.organization} onChange={(e) => handleArrayChange(setSecurityTraining)(index, "organization", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={10} sm={3}>
                          <TextField fullWidth label="Completion Date" type="date" value={trn.completionDate} onChange={(e) => handleArrayChange(setSecurityTraining)(index, "completionDate", e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                        </Grid>
                        <Grid item xs={2} sm={1}>
                          {securityTraining.length > 1 && <IconButton color="error" onClick={() => removeArrayItem(setSecurityTraining)(index)}><RemoveCircleOutlineIcon /></IconButton>}
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button startIcon={<AddCircleOutlineIcon />} onClick={addArrayItem(setSecurityTraining, { trainingName: "", organization: "", completionDate: "" })} size="small">Add Training</Button>
                  <SectionTitle>Licenses & Certifications</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.armedStatus}>
                        <InputLabel>Armed Status *</InputLabel>
                        <Select name="armedStatus" value={formData.armedStatus} onChange={handleChange} label="Armed Status *">
                          <MenuItem value="armed">Armed</MenuItem>
                          <MenuItem value="unarmed">Unarmed</MenuItem>
                        </Select>
                        {errors.armedStatus && <FormHelperText>{errors.armedStatus}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Guard Card / License Number *" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} error={!!errors.licenseNumber} helperText={errors.licenseNumber} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="License Expiry Date" name="licenseExpiry" type="date" value={formData.licenseExpiry} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="License State" name="licenseState" value={formData.licenseState} onChange={handleChange} />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">Firearms License</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.hasFirearmsLicense} onChange={handleChange} name="hasFirearmsLicense" />} label="I have a valid firearms license" />
                    </Grid>
                    {formData.hasFirearmsLicense && (
                      <>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="License Number" name="firearmsLicenseNumber" value={formData.firearmsLicenseNumber} onChange={handleChange} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="State" name="firearmsLicenseState" value={formData.firearmsLicenseState} onChange={handleChange} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Expiry Date" type="date" name="firearmsLicenseExpiry" value={formData.firearmsLicenseExpiry} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                      </>
                    )}
                  </Grid>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>First Aid Certification</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.hasFirstAid} onChange={handleChange} name="hasFirstAid" />} label="I have First Aid certification" />
                    </Grid>
                    {formData.hasFirstAid && (
                      <>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Organization" name="firstAidOrg" value={formData.firstAidOrg} onChange={handleChange} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Certification Date" type="date" name="firstAidDate" value={formData.firstAidDate} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Expiry Date" type="date" name="firstAidExpiry" value={formData.firstAidExpiry} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                      </>
                    )}
                  </Grid>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>CPR Certification</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.hasCPR} onChange={handleChange} name="hasCPR" />} label="I have CPR certification" />
                    </Grid>
                    {formData.hasCPR && (
                      <>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Organization" name="cprOrg" value={formData.cprOrg} onChange={handleChange} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Certification Date" type="date" name="cprDate" value={formData.cprDate} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                        <Grid item xs={12} sm={4}><TextField fullWidth label="Expiry Date" type="date" name="cprExpiry" value={formData.cprExpiry} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                      </>
                    )}
                  </Grid>
                  <SectionTitle>Background Information</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel control={<Checkbox checked={formData.hasCriminalHistory} onChange={handleChange} name="hasCriminalHistory" />} label="I have criminal history to disclose" />
                    </Grid>
                    {formData.hasCriminalHistory && (
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={3} label="Please provide details" name="criminalHistoryDetails" value={formData.criminalHistoryDetails} onChange={handleChange} />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel control={<Checkbox checked={formData.fingerprintingCompleted} onChange={handleChange} name="fingerprintingCompleted" />} label="Fingerprinting completed" />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={formData.backgroundCheckAuthorized} onChange={handleChange} name="backgroundCheckAuthorized" />}
                        label="I authorize Security Base Group to conduct a background check *"
                      />
                      {errors.backgroundCheckAuthorized && <FormHelperText error>{errors.backgroundCheckAuthorized}</FormHelperText>}
                    </Grid>
                  </Grid>
                  <SectionTitle>Health Disclosures (Optional)</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Physical Limitations (if any)" name="physicalLimitations" value={formData.physicalLimitations} onChange={handleChange} helperText="Describe any physical conditions that may affect job performance" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Mental Health Disclosure (if any)" name="mentalHealthDisclosure" value={formData.mentalHealthDisclosure} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={2} label="Substance Abuse History (if any)" name="substanceAbuseDisclosure" value={formData.substanceAbuseDisclosure} onChange={handleChange} />
                    </Grid>
                  </Grid>
                  <SectionTitle>Availability</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="How many hours per week can you work?" name="hoursPerWeek" type="number" value={formData.hoursPerWeek} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Available Start Date" name="availableStartDate" type="date" value={formData.availableStartDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Preferred Shifts</Typography>
                      {shifts.map(shift => (
                        <FormControlLabel key={shift} control={<Checkbox checked={preferredShifts.includes(shift)} onChange={() => toggleArrayValue(preferredShifts, setPreferredShifts, shift)} />} label={shift} />
                      ))}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Available Days</Typography>
                      {days.map(day => (
                        <FormControlLabel key={day} control={<Checkbox checked={availableDays.includes(day)} onChange={() => toggleArrayValue(availableDays, setAvailableDays, day)} />} label={day} />
                      ))}
                    </Grid>
                  </Grid>
                  <SectionTitle>Professional References</SectionTitle>
                  {references.map((ref, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Name" value={ref.name} onChange={(e) => handleArrayChange(setReferences)(index, "name", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Title" value={ref.title} onChange={(e) => handleArrayChange(setReferences)(index, "title", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Company" value={ref.company} onChange={(e) => handleArrayChange(setReferences)(index, "company", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Phone" value={ref.phone} onChange={(e) => handleArrayChange(setReferences)(index, "phone", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Email" value={ref.email} onChange={(e) => handleArrayChange(setReferences)(index, "email", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={10} sm={3}>
                          <TextField fullWidth label="Relationship" value={ref.relationship} onChange={(e) => handleArrayChange(setReferences)(index, "relationship", e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={2} sm={1}>
                          {references.length > 1 && <IconButton color="error" onClick={() => removeArrayItem(setReferences)(index)} sx={{ p: 0.5 }}><RemoveCircleOutlineIcon /></IconButton>}
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button startIcon={<AddCircleOutlineIcon />} onClick={addArrayItem(setReferences, { name: "", title: "", company: "", phone: "", email: "", relationship: "" })} size="small">Add Reference</Button>
                  <SectionTitle>Document Uploads</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Button variant="outlined" component="label" fullWidth sx={{ py: 1.5 }}>
                        {files.photo ? truncateFileName(files.photo.name) : "Upload Photo"}
                        <input type="file" hidden name="photo" accept="image/*" onChange={handleFileChange} />
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button variant="outlined" component="label" fullWidth sx={{ py: 1.5 }} color={errors.guardCardDocument ? "error" : "primary"}>
                        {files.guardCardDocument ? truncateFileName(files.guardCardDocument.name) : "Upload Guard Card *"}
                        <input type="file" hidden name="guardCardDocument" accept="image/*,.pdf" onChange={handleFileChange} />
                      </Button>
                      {errors.guardCardDocument && <FormHelperText error>{errors.guardCardDocument}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button variant="outlined" component="label" fullWidth sx={{ py: 1.5 }}>
                        {files.driverLicenseDocument ? truncateFileName(files.driverLicenseDocument.name) : "Upload Driver License"}
                        <input type="file" hidden name="driverLicenseDocument" accept="image/*,.pdf" onChange={handleFileChange} />
                      </Button>
                    </Grid>
                  </Grid>
                  <Box mt={4}>
                    <Button type="submit" variant="contained" size="large" fullWidth disabled={loading} sx={{ py: 1.5 }}>
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Application"}
                    </Button>
                  </Box>
                </form>
              </Paper>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
};

export default JobApplication;
